// SPDX-License-Identifier: Elastic-2.0
/**
 * onboardingSteps — declarative config for the 6-step wizard (T-013).
 *
 * Each entry tells `OnboardingPage` how to render and gate a step:
 *  - `id`             — stable analytics-friendly identifier
 *  - `labelKey`       — i18n key for the step title (currently unused
 *                       by chrome but available for breadcrumbs / a11y)
 *  - `canNext(state)` — pure predicate over `OnboardingPersisted` that
 *                       decides whether the "Continue" button is enabled
 *  - `hasSecondarySkip` — A5 only: shows the "Skip — set up later"
 *                       (account-less Free) button
 *  - `hideFooter`     — A6 only: footer is hidden (auto-advance happens)
 *  - `Body`           — `React.lazy` component for the step's content
 *
 * Lazy bodies (`React.lazy`) let Group 4 (T-014–T-019) drop in the real
 * implementations without changing this file's shape, and code-split
 * each step into its own chunk so first paint of A1 doesn't drag in
 * A2's WiFi scanner UI.
 *
 * The `STEPS` array length is the source of truth for "how many steps"
 * — keep it in sync with the Stepper / counter renders in `WizardShell`.
 */
import { lazy } from "react";
import type { ComponentType, LazyExoticComponent } from "react";

import type { OnboardingPersisted } from "./useOnboardingStore";

const A1Language = lazy(() => import("./steps/A1Language"));
const A2Network = lazy(() => import("./steps/A2Network"));
const A25Discovery = lazy(() => import("./steps/A25Discovery"));
const A26MatterAdd = lazy(() => import("./steps/A26MatterAdd"));
const A27ZigbeeAdd = lazy(() => import("./steps/A27ZigbeeAdd"));
const A3Location = lazy(() => import("./steps/A3Location"));
const A4Privacy = lazy(() => import("./steps/A4Privacy"));
const A5Account = lazy(() => import("./steps/A5Account"));
const A6MagicMoment = lazy(() => import("./steps/A6MagicMoment"));

export interface OnboardingStepConfig {
  id: "A1" | "A2" | "A2.5" | "A2.6" | "A2.7" | "A3" | "A4" | "A5" | "A6";
  labelKey: string;
  canNext: (state: OnboardingPersisted) => boolean;
  hasSecondarySkip?: boolean;
  hideFooter?: boolean;
  Body: LazyExoticComponent<ComponentType>;
}

/**
 * Per-step config. Order is significant — this drives both the Stepper
 * progress and the `OnboardingPage` step router (`STEPS[store.step]`).
 *
 * Gating predicates (`canNext`) live here so the chrome stays generic:
 *  - A1 needs a language picked
 *  - A2 needs network confirmed (ssid OR ethernet OR password)
 *  - A3 needs a location resolved
 *  - A4 is opt-in only, no gating
 *  - A5 has its own primary button (pair / use-local), but the chrome's
 *    next-button must always be enabled because the user may have
 *    completed pairing already; the body sets `auth.mode` and the
 *    parent advances
 *  - A6 hides the footer; the page never asks `canNext` for it
 */
export const STEPS: readonly OnboardingStepConfig[] = [
  {
    id: "A1",
    labelKey: "onboarding.a1.title",
    canNext: (s) => s.language !== null,
    Body: A1Language,
  },
  {
    id: "A2",
    labelKey: "onboarding.a2.title",
    canNext: (s) => s.network !== null,
    Body: A2Network,
  },
  {
    // A2.5 — Network discovery. The step body owns its own Continue / Skip
    // navigation (calls setStep directly, same as A5Account for pairing).
    // canNext returns false so the WizardShell footer primary button is
    // disabled — the step renders its own action row below the device list.
    id: "A2.5",
    labelKey: "onboarding.a25.title",
    canNext: () => false,
    hideFooter: true,
    Body: A25Discovery,
  },
  {
    // A2.6 — Optional "Add a Matter device now?" (F-352 / T-008). Inserted
    // AFTER A2.5, which shifts every later index by one: A3 3→4, A4 4→5,
    // A5 5→6, A6 6→7 (and TOTAL_STEPS grows from 7 to 8). The body owns its
    // own Skip / MatterFlow navigation (calls setStep directly, like A2.5),
    // so canNext is false + the footer is hidden. When the Matter controller
    // is unavailable the body self-skips on mount (auto-advances to A3) so
    // the user never sees a dead step — we keep it in the array for a stable
    // TOTAL_STEPS rather than conditionally splicing.
    id: "A2.6",
    labelKey: "onboarding.a26.title",
    canNext: () => false,
    hideFooter: true,
    Body: A26MatterAdd,
  },
  {
    // A2.7 — Optional "Add a Zigbee device now?" (F-353 / T-005). Inserted
    // AFTER A2.6, which shifts every later index by one again: A3 4→5, A4 5→6,
    // A5 6→7, A6 7→8 (and TOTAL_STEPS grows from 8 to 9). The body owns its own
    // Skip / ZigbeeFlow navigation (calls setStep directly, like A2.5/A2.6),
    // so canNext is false + the footer is hidden. When the Zigbee controller
    // is unavailable the body self-skips on mount (auto-advances to A3) so the
    // user never sees a dead step — we keep it in the array for a stable
    // TOTAL_STEPS rather than conditionally splicing.
    id: "A2.7",
    labelKey: "onboarding.a27.title",
    canNext: () => false,
    hideFooter: true,
    Body: A27ZigbeeAdd,
  },
  {
    id: "A3",
    labelKey: "onboarding.a3.title",
    canNext: (s) => s.location !== null,
    Body: A3Location,
  },
  {
    id: "A4",
    labelKey: "onboarding.a4.title",
    canNext: () => true,
    Body: A4Privacy,
  },
  {
    id: "A5",
    labelKey: "onboarding.a5.title",
    canNext: () => true,
    hasSecondarySkip: true,
    Body: A5Account,
  },
  {
    id: "A6",
    labelKey: "onboarding.a6.title",
    canNext: () => false,
    hideFooter: true,
    Body: A6MagicMoment,
  },
];

/** Total step count — single source of truth for Stepper + counter. */
export const TOTAL_STEPS = STEPS.length;
